// frontend/src/components/PhraseManager/PhraseManager.js
/**
 * Fraslexikon — användarens personliga fraser (triggerord -> text).
 * Lista, sök, lägg till, ändra, ta bort och importera från CSV (TakeCare).
 *
 * Props:
 *   onClose      stänger dialogen
 *   initialView  'list' (standard) eller 'import'
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  getPhrases,
  createPhrase,
  updatePhrase,
  deletePhrase,
  PHRASES_CHANGED_EVENT
} from '../JournalEditor/api/journalApi';
import PhraseImport from './PhraseImport';
import './PhraseManager.css';

function PhraseManager({ onClose, initialView = 'list' }) {
  // State
  const [activeTab, setActiveTab] = useState(initialView === 'import' ? 'import' : 'list');
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingPhrase, setEditingPhrase] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    phrase_text: ''
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Load phrases
  const loadPhrases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPhrases();
      setPhrases(data.personal_phrases || []);
    } catch (err) {
      console.error('Error loading phrases:', err);
      setError('Kunde inte ladda fraser');
    } finally {
      setLoading(false);
    }
  }, []);

  // Ladda vid öppning + när lexikonet ändrats (t.ex. efter import)
  useEffect(() => {
    loadPhrases();
    window.addEventListener(PHRASES_CHANGED_EVENT, loadPhrases);
    return () => window.removeEventListener(PHRASES_CHANGED_EVENT, loadPhrases);
  }, [loadPhrases]);

  // Stäng med Esc (formuläret först om det är öppet)
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      if (showForm) {
        setShowForm(false);
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showForm, onClose]);

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      phrase_text: ''
    });
    setFormError(null);
    setEditingPhrase(null);
    setShowForm(false);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (phrase) => {
    setFormData({
      title: phrase.title || '',
      phrase_text: phrase.phrase_text || ''
    });
    setFormError(null);
    setEditingPhrase(phrase);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setFormError('Triggerord krävs');
      return;
    }
    if (!formData.phrase_text.trim()) {
      setFormError('Text krävs');
      return;
    }

    try {
      setSaving(true);
      setFormError(null);

      if (editingPhrase) {
        await updatePhrase(editingPhrase.phrase_id, formData);
      } else {
        await createPhrase(formData);
      }

      await loadPhrases();
      resetForm();
    } catch (err) {
      console.error('Error saving phrase:', err);
      setFormError(err.response?.data?.error || 'Kunde inte spara frasen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (phrase) => {
    if (!window.confirm(`Vill du ta bort ;${phrase.title}?`)) {
      return;
    }

    try {
      await deletePhrase(phrase.phrase_id);
      await loadPhrases();
    } catch (err) {
      console.error('Error deleting phrase:', err);
      alert(err.response?.data?.error || 'Kunde inte ta bort frasen');
    }
  };

  // Filter phrases
  const query = searchQuery.toLowerCase();
  const filteredPhrases = query
    ? phrases.filter(p =>
        p.title.toLowerCase().includes(query) ||
        p.phrase_text.toLowerCase().includes(query)
      )
    : phrases;

  // Sort alphabetically by title
  const sortedPhrases = [...filteredPhrases].sort((a, b) =>
    a.title.localeCompare(b.title, 'sv')
  );

  return (
    <div className="phrase-manager-overlay" onClick={onClose}>
      <div className="phrase-manager" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="phrase-manager-header">
          <h2>Fraslexikon</h2>
          <button className="close-btn" onClick={onClose} aria-label="Stäng">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Info banner */}
        <div className="phrase-info-banner">
          <span>
            Skriv <kbd>;</kbd> följt av triggerordet i en journaltext för att infoga frasen.
            Exempel: <kbd>;knä</kbd> och Enter.
          </span>
        </div>

        {/* Tabs */}
        <div className="phrase-manager-tabs">
          <button
            className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            Mina fraser ({phrases.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            Importera
          </button>
        </div>

        {activeTab === 'import' ? (
          <div className="phrase-manager-content">
            <PhraseImport
              onDone={() => setActiveTab('list')}
              onCancel={() => setActiveTab('list')}
            />
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="phrase-manager-toolbar">
              <div className="search-box">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Sök fraser..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              <button className="add-btn" onClick={handleAddNew}>
                Ny fras
              </button>
            </div>

            {/* Content */}
            <div className="phrase-manager-content">
              {loading ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  Laddar fraser...
                </div>
              ) : error ? (
                <div className="error-state">
                  <i className="fas fa-exclamation-triangle"></i>
                  {error}
                  <button onClick={loadPhrases}>Försök igen</button>
                </div>
              ) : sortedPhrases.length === 0 ? (
                <div className="empty-state">
                  <p>
                    {searchQuery
                      ? 'Inga fraser matchar din sökning'
                      : 'Ditt fraslexikon är tomt. Skapa en fras eller importera ditt lexikon från TakeCare.'}
                  </p>
                  {!searchQuery && (
                    <div className="empty-state-actions">
                      <button onClick={handleAddNew}>
                        Skapa din första fras
                      </button>
                      <button onClick={() => setActiveTab('import')}>
                        Importera CSV
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <table className="phrase-table">
                  <thead>
                    <tr>
                      <th>Triggerord</th>
                      <th>Text</th>
                      <th>Åtgärder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPhrases.map(phrase => (
                      <tr key={phrase.phrase_id}>
                        <td className="phrase-title-cell">
                          <code>;{phrase.title}</code>
                        </td>
                        <td className="phrase-text-cell">
                          <div className="phrase-text-preview">
                            {phrase.phrase_text}
                          </div>
                        </td>
                        <td className="phrase-actions-cell">
                          <button
                            className="edit-btn"
                            onClick={() => handleEdit(phrase)}
                            title="Redigera"
                            aria-label={`Redigera ;${phrase.title}`}
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(phrase)}
                            title="Ta bort"
                            aria-label={`Ta bort ;${phrase.title}`}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="phrase-form-overlay" onClick={resetForm}>
            <div className="phrase-form" onClick={e => e.stopPropagation()}>
              <div className="phrase-form-header">
                <h3>{editingPhrase ? 'Redigera fras' : 'Ny fras'}</h3>
                <button className="close-btn" onClick={resetForm} aria-label="Stäng">
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="phrase-title">Triggerord *</label>
                  <div className="shortcut-input-wrapper">
                    <span className="shortcut-prefix">;</span>
                    <input
                      id="phrase-title"
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="t.ex. knä"
                      maxLength={100}
                      required
                      autoFocus
                    />
                  </div>
                  <span className="hint">
                    Skriv <kbd>;{formData.title || 'triggerord'}</kbd> i journalen för att infoga texten
                  </span>
                </div>

                <div className="form-group">
                  <label htmlFor="phrase-text">Text *</label>
                  <textarea
                    id="phrase-text"
                    name="phrase_text"
                    value={formData.phrase_text}
                    onChange={handleInputChange}
                    placeholder="Texten som infogas i journalen..."
                    rows={6}
                    required
                  />
                </div>

                {formError && (
                  <div className="phrase-form-error">
                    <i className="fas fa-exclamation-circle"></i>
                    {formError}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={resetForm}>
                    Avbryt
                  </button>
                  <button type="submit" className="save-btn" disabled={saving}>
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Sparar...
                      </>
                    ) : (
                      editingPhrase ? 'Spara' : 'Skapa'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhraseManager;
